import React, { useState, useMemo, useEffect, useRef } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// "first.last@wellversed.in" → "First Last"
const nameFromEmail = (email) => {
  if (!email) return 'Unknown';
  const local = String(email).split('@')[0] || email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ') || email;
};

// Deterministic bubble color per author — small delightful touch so
// each teammate has a recognizable accent in the thread. Uses a small
// palette chosen for good contrast on a light chat background.
const BUBBLE_TONES = [
  { bg: 'bg-emerald-500/10', border: 'border-emerald-200', name: 'text-emerald-700' },
  { bg: 'bg-sky-500/10',     border: 'border-sky-200',     name: 'text-sky-700' },
  { bg: 'bg-violet-500/10',  border: 'border-violet-200',  name: 'text-violet-700' },
  { bg: 'bg-amber-500/10',   border: 'border-amber-200',   name: 'text-amber-700' },
  { bg: 'bg-rose-500/10',    border: 'border-rose-200',    name: 'text-rose-700' },
  { bg: 'bg-teal-500/10',    border: 'border-teal-200',    name: 'text-teal-700' },
];
const toneForEmail = (email) => {
  if (!email) return BUBBLE_TONES[0];
  let h = 0;
  for (let i = 0; i < email.length; i++) h = ((h << 5) - h + email.charCodeAt(i)) | 0;
  return BUBBLE_TONES[Math.abs(h) % BUBBLE_TONES.length];
};

// Compact initials for the avatar circle. "First Last" → "FL".
const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Reusable "Remarks" cell rendered as a WhatsApp-style chat thread.
//
// - Cell body: shows a message-bubble icon, unread-style count of remarks,
//   and the last message snippet (or "Add remark" placeholder).
// - Popover: full thread scrollable, each entry a bubble with author name,
//   time (relative + absolute on hover), and legacy tag when appropriate.
//   Bottom sticky input — Enter to post, Shift+Enter for a new line.
//
// Legacy (single-string) remarks are lazy-migrated by the backend on the
// first append; while none exist yet, the cell falls back to `task.remarks`
// as a single read-only bubble so nothing appears lost.
export const RemarksThreadCell = ({ task, onThreadUpdate }) => {
  const { user: currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState(task?.remarks_thread || []);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Sync when parent hands over a new task snapshot.
  useEffect(() => {
    setThread(task?.remarks_thread || []);
  }, [task?.id, task?.remarks_thread]);

  // Auto-scroll to the newest message when the popover opens or a
  // message lands.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, thread.length]);

  // Derived: if there's no thread but a legacy string remark exists, show
  // that string as a single non-editable bubble so the user sees history.
  const displayThread = useMemo(() => {
    if (thread && thread.length > 0) return thread;
    const legacy = (task?.remarks || '').trim();
    if (!legacy) return [];
    return [{
      id: 'legacy-preview',
      author_email: task?.submitter_email || 'unknown',
      author_name: nameFromEmail(task?.submitter_email),
      text: legacy,
      created_at: task?.updated_at || task?.created_at || new Date().toISOString(),
      legacy: true,
      preview: true,
    }];
  }, [thread, task]);

  const totalCount = displayThread.length;
  const lastMsg = totalCount > 0 ? displayThread[totalCount - 1] : null;

  const postMessage = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.id}/remarks/thread`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to post');
      }
      const data = await res.json();
      setThread(data.thread || []);
      setDraft('');
      // Bubble the latest thread back up so parent state stays in sync
      // (Task Log's search / filters read from `task.remarks`).
      if (onThreadUpdate) {
        onThreadUpdate(task.id, {
          remarks_thread: data.thread,
          remarks: (data.message && data.message.text) || task.remarks,
        });
      }
    } catch (e) {
      toast.error(e.message || 'Failed to post remark');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      postMessage();
    }
  };

  const meEmail = (currentUser?.email || '').toLowerCase();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'group flex items-center gap-1.5 h-7 max-w-[180px] px-2 rounded-md border border-dashed transition-all',
            totalCount > 0
              ? 'border-slate-200 bg-white hover:border-[#2EB4AC] hover:bg-[#2EB4AC]/5 text-slate-700'
              : 'border-slate-200 bg-white hover:border-slate-300 text-slate-400'
          )}
          data-testid={`remarks-thread-trigger-${task.task_id || task.id}`}
        >
          <MessageSquare className={cn('h-3.5 w-3.5 shrink-0', totalCount > 0 ? 'text-[#2EB4AC]' : 'text-slate-400')} />
          {totalCount > 0 ? (
            <>
              <span className="text-xs font-medium truncate" title={lastMsg?.text}>{lastMsg?.text}</span>
              <span className="ml-auto text-[10px] font-semibold bg-slate-100 text-slate-600 rounded-full px-1.5 py-px group-hover:bg-[#2EB4AC]/15 group-hover:text-[#2EB4AC]">
                {totalCount}
              </span>
            </>
          ) : (
            <span className="text-xs italic">Add remark</span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[380px] p-0 overflow-hidden border-slate-200 shadow-lg" data-testid={`remarks-thread-popover-${task.task_id || task.id}`}>
        {/* Header — brand teal accent bar so the thread feels part of PRISM */}
        <div className="px-3.5 py-2.5 border-b border-slate-100 bg-gradient-to-r from-[#2EB4AC]/8 to-white flex items-center gap-2">
          <div className="h-6 w-6 rounded-lg bg-[#2EB4AC]/15 flex items-center justify-center">
            <MessageSquare className="h-3.5 w-3.5 text-[#2EB4AC]" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-800 leading-tight">Remarks</p>
            <p className="text-[10px] text-slate-400 leading-tight">{totalCount === 0 ? 'Start the conversation' : `${totalCount} ${totalCount === 1 ? 'message' : 'messages'}`}</p>
          </div>
        </div>

        {/* Thread — modern chat bubbles on a neutral canvas */}
        <div
          ref={scrollRef}
          className="max-h-[300px] min-h-[100px] overflow-y-auto bg-white px-3 py-3 space-y-0.5"
          data-testid="remarks-thread-list"
          style={{
            backgroundImage: 'radial-gradient(rgba(46,180,172,0.06) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }}
        >
          {displayThread.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="h-9 w-9 rounded-full bg-[#2EB4AC]/10 flex items-center justify-center mb-2">
                <MessageSquare className="h-4 w-4 text-[#2EB4AC]" />
              </div>
              <p className="text-xs font-medium text-slate-500">No remarks yet</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Type below to start the conversation</p>
            </div>
          ) : (
            displayThread.map((m, idx) => {
              const mine = (m.author_email || '').toLowerCase() === meEmail && !m.preview;
              const tone = toneForEmail(m.author_email);
              const created = m.created_at ? new Date(m.created_at) : null;
              // Group consecutive messages from the same author — only the
              // first bubble in a run shows the avatar + name; the rest sit
              // flush underneath for a tight conversation rhythm.
              const prev = displayThread[idx - 1];
              const next = displayThread[idx + 1];
              const sameAsPrev = prev && (prev.author_email || '').toLowerCase() === (m.author_email || '').toLowerCase();
              const sameAsNext = next && (next.author_email || '').toLowerCase() === (m.author_email || '').toLowerCase();
              const isFirstInGroup = !sameAsPrev;
              const isLastInGroup = !sameAsNext;
              return (
                <div
                  key={m.id}
                  className={cn(
                    'flex items-end gap-1.5',
                    mine ? 'flex-row-reverse' : 'flex-row',
                    isFirstInGroup ? 'mt-2' : 'mt-0.5'
                  )}
                  data-testid={`remarks-thread-msg-${m.id}`}
                >
                  {/* Avatar — only for the last message in a group to keep the thread airy */}
                  <div className="w-6 shrink-0">
                    {isLastInGroup && (
                      <div
                        className={cn(
                          'h-6 w-6 rounded-full flex items-center justify-center text-[9px] font-bold border shadow-sm',
                          mine ? 'bg-[#2EB4AC] text-white border-[#2EB4AC]/50' : `${tone.bg} ${tone.name} ${tone.border}`
                        )}
                        title={m.author_email}
                      >
                        {initialsOf(m.author_name || nameFromEmail(m.author_email))}
                      </div>
                    )}
                  </div>

                  {/* Bubble column */}
                  <div className={cn('max-w-[260px] min-w-0', mine ? 'items-end' : 'items-start', 'flex flex-col')}>
                    {/* Sender name — only above the first bubble in a group */}
                    {isFirstInGroup && (
                      <div className={cn('flex items-baseline gap-1.5 mb-1 px-0.5', mine ? 'justify-end' : 'justify-start')}>
                        <span className={cn('text-[10px] font-semibold', mine ? 'text-[#2EB4AC]' : tone.name)}>
                          {mine ? 'You' : (m.author_name || nameFromEmail(m.author_email))}
                        </span>
                        {m.legacy && (
                          <span className="text-[8px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded px-1 leading-tight">legacy</span>
                        )}
                      </div>
                    )}

                    {/* Message bubble */}
                    <div
                      className={cn(
                        'text-xs leading-relaxed px-3 py-2 whitespace-pre-wrap break-words shadow-sm',
                        mine
                          ? 'bg-[#2EB4AC] text-white border border-[#2EB4AC]/60'
                          : 'bg-white text-slate-800 border border-slate-200',
                        // Chat-bubble corner shaping — outer corners rounded,
                        // inner corner (near the avatar tail) tightened when
                        // this is the last in the group.
                        'rounded-2xl',
                        mine
                          ? (isLastInGroup ? 'rounded-br-md' : '')
                          : (isLastInGroup ? 'rounded-bl-md' : '')
                      )}
                    >
                      {m.text}
                    </div>

                    {/* Timestamp — only under the last bubble in a group */}
                    {isLastInGroup && created && (
                      <span
                        className={cn('text-[9px] text-slate-400 mt-0.5 px-1')}
                        title={format(created, 'PPpp')}
                      >
                        {formatDistanceToNow(created, { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Composer — clean, single input with an integrated teal send button */}
        <div className="border-t border-slate-100 bg-white px-2.5 py-2">
          <div className="flex items-end gap-2 bg-slate-50 border border-slate-200 rounded-full pl-3.5 pr-1 py-1 focus-within:border-[#2EB4AC] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#2EB4AC]/15 transition-all">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type a remark…"
              className="flex-1 resize-none text-xs bg-transparent border-0 focus:outline-none focus:ring-0 py-1.5 min-h-[20px] max-h-[80px] placeholder:text-slate-400"
              rows={1}
              data-testid={`remarks-thread-input-${task.task_id || task.id}`}
            />
            <button
              type="button"
              onClick={postMessage}
              disabled={!draft.trim() || sending}
              className={cn(
                'h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-all',
                draft.trim() && !sending
                  ? 'bg-[#2EB4AC] text-white hover:bg-[#269d96] shadow-sm hover:scale-105'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              )}
              data-testid={`remarks-thread-send-${task.task_id || task.id}`}
              title="Send (Enter). Shift+Enter for new line"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="text-[9px] text-slate-400 text-center mt-1">Enter to send · Shift+Enter for new line</p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RemarksThreadCell;
